import React, { FC, useMemo, useState } from "react";

import {
  Campaign,
  CampaignBody,
  CampaignType,
  SiteItem,
} from "@vviinn/user-managment-client-ts";

import { isActive } from "../../../../helpers";
import { calculateNewConfig } from "../../helpers";
import { allowedInfluencers } from "../modals/constants";
import { getCampaignValue } from "../modals/helpers";
import { Fields } from "../../../../ui/layout/detailedPages/Fields";
import { Restricted } from "../../../../components/Restricted";
import { QueryRow } from "./QueryRow";
import { HandleCampaignChange } from "../../../types";
import { similarityNumericSettings } from "../../../constants";
import { relevantCampaignNets } from "../../../helpers/relevantCampaignNets";
import { AcceptableNamedRow } from "../../../../ui/layout/organisms/SlideOver/SlideOverFixedLayouts/AcceptableNamedRow";
import { detailsBlockStyles } from "../../../../ui/layout/detailedPages/detailsBlockStyles";
import { CollapsedFields } from "../../../../components/GeneralUI/CollapsedFields/CollapsedFields";
import { NetsKeys, RenderNumericNet } from "../RenderNumericNet";
import { ResetCampaign } from "../ResetCampaign/ResetCampaign";
import { UpdateCampaignProps } from "../../types";
import { CampaignNumberic } from "../../../components/CampaignNumberic";
import { NamedSwitch } from "../../../../ui/layout/molecules/NamedSwitch";
import { parseNumberOrZero } from "../../../../helpers";

type MethodsProps = UpdateCampaignProps & {
  siteDetails?: SiteItem;
};

type Options = Array<{
  title: string;
  message?: string;
  nets: { net: NetsKeys; value?: number }[];
}>;

const netsData = (campaign: Campaign, nets: NetsKeys[] = []) =>
  nets.map((net) => ({
    net,
    value: parseNumberOrZero(String(getCampaignValue(net, campaign))),
  }));

const SEMANTIC_SEARCH_TITLE = "Semantic search";
const { detailsSwitchableStyles, customWrapperStyles } =
  detailsBlockStyles(true);

export const Methods: FC<React.PropsWithChildren<MethodsProps>> = ({
  campaign,
  siteDetails,
  updateCampaignBody,
}) => {
  const [config, setConfig] = useState<CampaignBody>({});

  const initConfig = () => setConfig({});

  const relevantNets = useMemo(() => {
    if (!siteDetails) return {};
    return relevantCampaignNets(campaign.type, siteDetails);
  }, [siteDetails, campaign.type]);

  const campaignType: CampaignType = campaign.type;
  const isTextSearchCampaign = campaignType === "TEXT_SEARCH";

  const similarityNetsData = useMemo(
    () => [
      ...netsData(campaign, relevantNets.relevantTextNets),
      ...netsData(campaign, relevantNets.relevantSimilarities),
    ],
    [
      campaign,
      relevantNets.relevantSimilarities,
      relevantNets.relevantTextNets,
    ],
  );

  const colorNetsData = useMemo(
    () => netsData(campaign, relevantNets.relevantColors),
    [campaign, relevantNets.relevantColors],
  );

  const handleChange: HandleCampaignChange =
    (key, isNumberSettings) => (value) => {
      let campaignValue = getCampaignValue(key, campaign);
      setConfig(
        calculateNewConfig(isNumberSettings, config, key, value, campaignValue),
      );
    };

  const updateCampaignField = async () => updateCampaignBody(config);

  const options: Options = [
    {
      title: "Semantic Search",
      nets: similarityNetsData,
    },
  ];
  if (!isTextSearchCampaign) {
    options.push({
      title: "Color",
      message: relevantNets.relevantColors?.length
        ? undefined
        : "This site doesn't support color Nets.",
      nets: colorNetsData,
    });
  }

  const allowSubmit = useMemo(() => !!Object.keys(config).length, [config]);

  return (
    <Fields title="Methods">
      {options.map(
        (value, index) =>
          allowedInfluencers[
            campaignType as keyof typeof allowedInfluencers
          ]?.includes(value.title) && (
            <AcceptableNamedRow
              key={index}
              title={value.title}
              content={isActive(
                !!value.nets.filter(({ value }) => !!value).length,
              )}
              permissionsGroup="campaign:basic_config"
              initConfig={initConfig}
              onSubmit={updateCampaignField}
              allowSubmit={allowSubmit}
              message={value.message}
            >
              {value.nets.map(({ net, value }) => (
                <RenderNumericNet
                  net={net}
                  handleChange={handleChange}
                  value={value}
                  key={net}
                />
              ))}
              {value.title === "Semantic Search" && (
                <CollapsedFields
                  title={SEMANTIC_SEARCH_TITLE}
                  isSwitchable={false}
                  active={true}
                  customGroupStyles={detailsSwitchableStyles}
                  customWrapperStyles={customWrapperStyles}
                >
                  {similarityNumericSettings.map((key) => (
                    <CampaignNumberic
                      campaignKey={key}
                      key={key}
                      campaign={campaign}
                      handleChange={handleChange}
                    />
                  ))}
                </CollapsedFields>
              )}
              <ResetCampaign
                campaign={campaign}
                updateCampaignBody={updateCampaignBody}
              />
            </AcceptableNamedRow>
          ),
      )}
      {isTextSearchCampaign && (
        <Restricted permissions="campaign:basic_config:view">
          <NamedSwitch
            title="Auto-Suggest"
            permissions="campaign:basic_config:update"
            value={campaign.autoSuggestCampaign?.active ?? false}
            onChange={(value) =>
              updateCampaignBody({
                autoSuggestCampaign: {
                  ...campaign.autoSuggestCampaign,
                  active: value,
                },
              })
            }
          />
          <QueryRow
            queries={(
              campaign.autoSuggestCampaign?.queryMapping ||
              campaign.queryMapping ||
              []
            ).map(({ query, target }) => ({
              query,
              url: target?.url ?? "",
            }))}
            updateCampaignBody={(updatedProps: CampaignBody) =>
              updateCampaignBody({
                autoSuggestCampaign: {
                  ...campaign.autoSuggestCampaign,
                  queryMapping: updatedProps.autoSuggestCampaign?.queryMapping,
                },
              })
            }
          />
          <NamedSwitch
            title="Query-to-Filter Automation"
            permissions="campaign:basic_config:update"
            value={campaign.queryToFilterMappingActive ?? false}
            onChange={(value) =>
              updateCampaignBody({ queryToFilterMappingActive: value })
            }
          />
          <NamedSwitch
            title="Smart Reranking"
            permissions="campaign:basic_config:update"
            value={campaign.smartRerankingActive ?? false}
            onChange={(value) =>
              updateCampaignBody({ smartRerankingActive: value })
            }
          />
        </Restricted>
      )}
    </Fields>
  );
};
